import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contacts');
  const navigate = useNavigate();


  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    fetchData(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [contactsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/projects`, { headers })
      ]);
      setContacts(contactsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      alert('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg" style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  const tabStyle = (isActive) => ({
    backgroundColor: isActive ? 'var(--text-primary)' : 'transparent',
    color: isActive ? 'var(--bg-primary)' : 'var(--text-primary)',
    border: `1px solid ${isActive ? 'var(--text-primary)' : 'var(--border-strong)'}`,
    fontFamily: 'Nohemi, sans-serif',
  });

  return (
    <div className="min-h-screen pt-20 pb-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 flex items-center justify-between">
          <h1
            className="text-3xl font-bold"
            style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
          >
            Admin Dashboard
          </h1>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium border rounded-full transition-all hover:opacity-80"
            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}
            data-cursor-hover
            data-testid="admin-logout-button"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex gap-3">
          <button
            onClick={() => setActiveTab('contacts')}
            className="px-5 py-2 text-sm uppercase tracking-wide transition-all rounded-full"
            style={tabStyle(activeTab === 'contacts')}
            data-cursor-hover
          >
            Leads ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className="px-5 py-2 text-sm uppercase tracking-wide transition-all rounded-full"
            style={tabStyle(activeTab === 'projects')}
            data-cursor-hover
          >
            Projects ({projects.length})
          </button>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="p-8 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}>
              Contact Submissions
            </h2>
            {contacts.length === 0 ? (
              <p className="text-base" style={{ color: 'var(--text-tertiary)' }}>No contact submissions yet.</p>
            ) : (
              <div className="space-y-6">
                {contacts.map((contact) => (
                  <div key={contact.id} className="pb-6" style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{contact.name}</h3>
                      <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        {new Date(contact.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{contact.email}</p>
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{contact.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="p-8 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)', fontFamily: 'Nohemi, sans-serif' }}>
              Projects
            </h2>
            <div className="space-y-4">
              {projects.map((project) => (
                <div key={project.id} className="flex items-center justify-between pb-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div className="flex-1">
                    <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{project.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{project.client} &middot; {project.category}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span
                      className="px-3 py-1 text-xs rounded-full"
                      style={{
                        backgroundColor: project.featured ? 'var(--text-primary)' : 'var(--bg-tertiary)',
                        color: project.featured ? 'var(--bg-primary)' : 'var(--text-secondary)',
                        fontFamily: 'Nohemi, sans-serif',
                      }}
                    >
                      {project.featured ? 'Featured' : 'Standard'}
                    </span>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      data-cursor-hover
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                <strong>Note:</strong> To add or edit projects, update seed_data.py and run it again.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
